import { useCallback, useEffect, useRef, useState } from 'react';
import { createDxReportDraft, updateDxReportDraft } from '../../../../../services/clinicalDrafts';
import { DRAFT_GONE_MESSAGE, isDraftGone404 } from '../../../../../services/clinicalDraftGone';
import type { TimelineEvent } from '../../types';
import { useToast } from '../../../../../providers/ToastProvider';
import { useInvalidateDxReportDrafts } from '../../../../../hooks/useDxReportDrafts';

interface DraftBody {
    formData: Record<string, unknown>;
    groupId?: string;
    imageFileIds?: string[];
}

interface UseDxDraftLifecycleParams {
    patientId: string;
    resumingDraft?: TimelineEvent;
    /** Pull the current draft body each time we need to save - kept as a
     *  callback so we always read the freshest payload without re-binding
     *  effects every render. */
    buildDraftBody: () => DraftBody;
    /** True when the local payload has unsaved changes vs. the initial. */
    isDirty: boolean;
    /** Step navigation triple - any change triggers an auto-save fire
     *  (excluding navigation INTO the review preview step). */
    stepIndex: number;
    clinicalSubIndex: number;
    recommendationSubIndex: number;
    isOnReviewStep: boolean;
    /** Called after the host-surface invokes the outer back/close so the
     *  hook can flush a final auto-save before unmount. */
    onBack: () => void;
    /** Work-queue TaskSurface registers a beforeClose handler so the wrapper
     *  awaits a save before navigating away. Pass-through. */
    onRegisterBeforeClose?: (handler: (() => Promise<void>) | null) => void;
}

interface UseDxDraftLifecycleResult {
    currentDraftId: string | null;
    setCurrentDraftId: (id: string | null) => void;
    currentDraftIdRef: React.MutableRefObject<string | null>;
    isSavingDraft: boolean;
    isSavingDraftSilent: boolean;
    showSavedBadge: boolean;
    handleSaveDraft: () => Promise<void>;
    handleSaveDraftSilent: () => Promise<void>;
    flushDraft: () => Promise<boolean>;
    handleBack: () => Promise<void>;
}

/**
 * Owns the draft-save lifecycle for the Dx report editor:
 *  - tracks the persisted draft id (server-assigned on first save)
 *  - exposes loud `handleSaveDraft` (toast-on-success) and silent
 *    `handleSaveDraftSilent` (used by auto-save paths)
 *  - fires silent save on step navigation, on outer-close, and on
 *    component unmount via the parent-supplied beforeClose hook
 *  - shows a brief "Saved ✓" badge for 2s after a successful silent save
 */
export function useDxDraftLifecycle({
    patientId,
    resumingDraft,
    buildDraftBody,
    isDirty,
    stepIndex,
    clinicalSubIndex,
    recommendationSubIndex,
    isOnReviewStep,
    onBack,
    onRegisterBeforeClose
}: UseDxDraftLifecycleParams): UseDxDraftLifecycleResult {
    const { showToast } = useToast();
    const invalidateDrafts = useInvalidateDxReportDrafts(patientId);

    const [currentDraftId, setCurrentDraftId] = useState<string | null>(
        () => resumingDraft?.id ?? null
    );
    const currentDraftIdRef = useRef(currentDraftId);
    currentDraftIdRef.current = currentDraftId;

    const [isSavingDraft, setIsSavingDraft] = useState(false);
    const [isSavingDraftSilent, setIsSavingDraftSilent] = useState(false);
    const [showSavedBadge, setShowSavedBadge] = useState(false);
    const savedBadgeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(
        () => () => {
            if (savedBadgeTimeoutRef.current) clearTimeout(savedBadgeTimeoutRef.current);
        },
        []
    );

    // Flips true when the server reports the draft gone (promoted by a submit
    // elsewhere, or discarded by a teammate). Silent auto-save disarms -
    // blindly recreating on a timer would resurrect a duplicate of a possibly
    // already-submitted document - but explicit user actions (Save draft,
    // flush-before-submit) re-create a fresh draft and re-arm. The stale id is
    // cleared because promoteDraft's idempotent-replay branch ACCEPTS an
    // already-promoted id and re-applies the payload: reusing it would
    // silently overwrite the teammate's submitted document, not fail.
    const draftGoneRef = useRef(false);
    // One-time notice guard: the silent path surfaces the vanished draft
    // exactly once instead of on every auto-save retry.
    const draftGoneNotifiedRef = useRef(false);

    const persistDraft = useCallback(
        async (allowRecreate = false): Promise<void> => {
            if (draftGoneRef.current && !allowRecreate) throw new Error(DRAFT_GONE_MESSAGE);
            const body = buildDraftBody();
            const draftId = currentDraftIdRef.current;
            if (draftId) {
                try {
                    await updateDxReportDraft(patientId, draftId, body);
                } catch (err) {
                    if (!isDraftGone404(err)) throw err;
                    draftGoneRef.current = true;
                    setCurrentDraftId(null);
                    currentDraftIdRef.current = null;
                    throw new Error(DRAFT_GONE_MESSAGE);
                }
            } else {
                const event = await createDxReportDraft(patientId, body);
                setCurrentDraftId(event.id);
                // A fresh draft exists again - re-arm auto-save and allow a
                // future gone-notice if it happens to this draft too.
                draftGoneRef.current = false;
                draftGoneNotifiedRef.current = false;
            }
            invalidateDrafts();
        },
        [buildDraftBody, patientId, invalidateDrafts]
    );

    const handleSaveDraft = useCallback(async () => {
        setIsSavingDraft(true);
        try {
            // Explicit user action: allowed to re-create after draft-gone.
            await persistDraft(true);
            showToast('Draft saved.', 'success');
        } catch (err) {
            const msg = err instanceof Error ? err.message : 'Failed to save draft.';
            showToast(msg, 'error');
        } finally {
            setIsSavingDraft(false);
        }
    }, [persistDraft, showToast]);

    const flushDraft = useCallback(async (): Promise<boolean> => {
        setIsSavingDraftSilent(true);
        try {
            // Explicit user intent (save-and-exit): allowed to re-create after
            // draft-gone so the flow never dead-ends.
            await persistDraft(true);
            return true;
        } catch (err) {
            const msg = err instanceof Error ? err.message : 'Failed to save draft.';
            showToast(msg, 'error');
            return false;
        } finally {
            setIsSavingDraftSilent(false);
        }
    }, [persistDraft, showToast]);

    const handleSaveDraftSilent = useCallback(async () => {
        setIsSavingDraftSilent(true);
        try {
            await persistDraft();
            // Show brief "Saved ✓" badge for 2s then fade out.
            if (savedBadgeTimeoutRef.current) clearTimeout(savedBadgeTimeoutRef.current);
            setShowSavedBadge(true);
            savedBadgeTimeoutRef.current = setTimeout(() => setShowSavedBadge(false), 2000);
        } catch (err) {
            // Auto-save failures are non-blocking, but a vanished draft means
            // edits stopped persisting - the user must hear that once.
            const msg = err instanceof Error ? err.message : '';
            if (msg === DRAFT_GONE_MESSAGE && !draftGoneNotifiedRef.current) {
                draftGoneNotifiedRef.current = true;
                showToast(DRAFT_GONE_MESSAGE, 'error');
            }
        } finally {
            setIsSavingDraftSilent(false);
        }
    }, [persistDraft, showToast]);

    const handleBack = useCallback(async () => {
        // Auto-save on exit fires for BOTH brand-new drafts AND new-version drafts.
        if (isDirty) {
            try {
                await handleSaveDraftSilent();
            } catch {
                // swallow - don't block exit on save failure
            }
        }
        onBack();
    }, [isDirty, handleSaveDraftSilent, onBack]);

    // Auto-save on step navigation. Skip when navigating INTO the review
    // step since the preview generates a PDF and we don't want a phantom
    // save in the middle.
    const prevStepRef = useRef<
        { step: number; clinicalSub: number; recommendationSub: number } | undefined
    >(undefined);
    useEffect(() => {
        const prev = prevStepRef.current;
        const cur = {
            step: stepIndex,
            clinicalSub: clinicalSubIndex,
            recommendationSub: recommendationSubIndex
        };
        if (
            prev !== undefined &&
            isDirty &&
            !isOnReviewStep &&
            (prev.step !== cur.step ||
                prev.clinicalSub !== cur.clinicalSub ||
                prev.recommendationSub !== cur.recommendationSub)
        ) {
            void handleSaveDraftSilent();
        }
        prevStepRef.current = cur;
    }, [
        stepIndex,
        clinicalSubIndex,
        recommendationSubIndex,
        isDirty,
        isOnReviewStep,
        handleSaveDraftSilent
    ]);

    // Expose a flush-before-close handler to a hosting surface (e.g.
    // work-queue TaskSurface). Re-registers on every dep change so the
    // wrapper always awaits the latest isDirty/handler snapshot.
    useEffect(() => {
        if (!onRegisterBeforeClose) return;
        onRegisterBeforeClose(async () => {
            if (isDirty) {
                await handleSaveDraftSilent();
            }
        });
        return () => onRegisterBeforeClose(null);
    }, [onRegisterBeforeClose, isDirty, handleSaveDraftSilent]);

    return {
        currentDraftId,
        setCurrentDraftId,
        currentDraftIdRef,
        isSavingDraft,
        isSavingDraftSilent,
        showSavedBadge,
        handleSaveDraft,
        handleSaveDraftSilent,
        flushDraft,
        handleBack
    };
}
